// composables/useItemIssue.js
//
// The member's decision state machine for one item-issue (member-answerable
// collection issues, Phase 2 + interaction-v2). Orchestrates the backend
// contract via itemIssue.service.js and owns the screen's lifecycle states.
//
// Interaction-v2 widened this from "one question, one answer, one screen" to a
// small READ+WRITE surface, because the backend widened underneath it:
//   • `show` no longer 403s once the issue leaves awaiting_member, so a
//     successful fetch no longer implies "answerable" — every state derives from
//     the issue's own `status` + `memberResponse` (see isIssueAnswerable).
//   • The answer is REVISABLE (latest-wins) until the leader applies it, so the
//     form is prefilled from memberResponse/memberComment/memberOptionIndex, so
//     a revision never forces a re-pick — and re-submitting is a first-class
//     flow, not a double-tap to guard against.
// The 2026-08-10 redesign then collapsed the card itself: the leader asks a
// `question` in their own words and lists `options` they can do instead; she
// answers yes / no / wait, optionally picking one option BY INDEX. The
// per-reason taxonomy (offeredVariants, new/oldPriceCents, availableQty and the
// six-case response matrix) is gone — see ../backend/_bmad-output/sync/briefs/
// 2026-08-10-item-issue-redesign-question-and-three-answers.md.
//   • `timeline` carries the full member-safe history, so the detail is worth
//     showing in every state — including terminal ones.
// Still SERVER-CONFIRMED, never optimistic: an answer can lose a race to a
// leader-apply, and a false "rescued" flash would break trust.
//
// States (`state` ref):
//   'loading'         initial fetch in flight
//   'awaiting'        the answer form is live and submittable. Covers BOTH the
//                     first answer (status awaiting_member) and a REVISION
//                     (status open + an answer already recorded) — `isRevision`
//                     tells them apart for copy/prefill.
//   'submitting'      a response POST is in flight (inputs lock, button spins)
//   'read-only'       readable but NOT answerable: the issue is terminal
//                     (resolved), or it's open with no answer yet (the leader
//                     raised it but hasn't sent it). Detail + timeline, no form.
//   'already-handled' 403/404: not ours, or the flag is off — a calm end-state,
//                     NOT an error, do NOT retry
//   'error'           network / 5xx on the initial load — retryable
import { useRepo } from 'pinia-orm';
import { computed, ref } from 'vue';
import {
  ISSUE_STATUS,
  isIssueAnswerable,
  issuePillVariant,
  terminalOutcomeIsNeutral,
  terminalOutcomeKey,
  timelineSteps,
} from '@/constants/collectionIssue.js';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import { ItemStatusCategories } from '@/enums/commerce/ItemStatusConfig.js';
import OrderBox from '@/models/commerce/OrderBox.js';
import OrderBoxItem from '@/models/commerce/OrderBoxItem.js';
import {
  fetchItemIssue,
  ITEM_ISSUE_ALREADY_HANDLED,
  ITEM_ISSUE_BLOCKED,
  ITEM_ISSUE_PLAN_CLOSED,
  respondToItemIssue,
} from '@/services/itemIssue.service.js';
import { cancelOrderBoxItem } from '@/services/orderBoxItem.service.js';
import { itemDisplayName } from '@/utils/orderBoxItemDisplay.js';
import { boxCanTakeItBack } from '@/utils/supplierSuggestion.js';

/**
 * The ONE confirmation, for every answer (owner decision D2, 2026-08-11).
 *
 * There used to be three, one per response — "we'll bring it to you", "next
 * collection", "it came off your order". Every one of them stated an OUTCOME,
 * and she cannot cause an outcome: the admin keeps all four close verbs on
 * every question whatever she answered (InteractsWithPlanIssues.php:252-330).
 * The worked failure is `confirm.declined` — she taps «إلغاء الصنف», reads that
 * the item was cancelled, the admin collects it anyway as they are entitled to,
 * and she is billed for something the app told her was gone.
 *
 * So the close names no outcome and moves no money. It says the true thing:
 * her answer reached us and a human has it. That was already the copy for a
 * collected item, where all three outcome lines were plainly false; D2 makes it
 * the close for every path, which is what retires the other three.
 */
const RECORDED_CONFIRM_KEY = 'collection.confirm.recorded';

// A REVISION still confirms differently: "we've recorded your answer" after she
// deliberately CHANGED one reads as though nothing happened. "We updated your
// answer" is the honest confirmation, and it states a fact about the record
// rather than an outcome, so D2 leaves it standing.
const REVISED_CONFIRM_KEY = 'collection.answer.updated';

export function useItemIssue() {
  const issueId = ref(null);
  const issue = ref(null); // deserialized ItemIssue (+ nested orderBoxItem)
  const state = ref('loading');
  const loadError = ref(null); // Error for the retryable 'error' state
  const respondError = ref(null); // inline blocking message (backend Arabic detail)
  const actionsLocked = ref(false); // true once a refusal is confirmed non-retryable (see respond())
  // The server has REPORTED this round closed (a `plan-closed` 409 on respond —
  // since close-now, 2026-09-24, no other 409 means that), which is a
  // strictly wider fact than `actionsLocked` — that one additionally requires the
  // confirming re-read to have succeeded before it will kill the buttons.
  //
  // The two need to be separate because they answer different questions.
  // Disabling the controls is destructive, so it fails OPEN: if we could not
  // confirm, the member keeps their buttons. Withdrawing a CLAIM about the round
  // is not destructive, so it fails CLOSED: the moment the server says "closed",
  // the card must stop telling the member to answer before it closes, and stop
  // promising they can still revise — whether or not the follow-up read landed.
  // Collapsing these into one flag forces one of the two to be wrong.
  //
  // Deliberately NOT reset in respond(): a round the server has already refused
  // does not re-open mid-screen, and re-arming it per tap would flash the claim
  // back on between a retry and its refusal. Only a fresh load() clears it.
  const roundReportedClosed = ref(false);
  const submittedResponse = ref(null); // which response won (drives confirmKey)
  const submittedRevision = ref(false); // was that submit a revision, not a first answer?
  // An answer landed on THIS screen, just now — the card flips in place and
  // shows `confirmKey` inline instead of crossing to a confirm screen.
  //
  // It is deliberately NOT a state: the card is answerable again the instant the
  // POST returns (latest-wins until the leader applies), so the state machine
  // rightly rests back on 'awaiting' and the confirmation is a fact layered on
  // top of it — not a mode that has to be left. The card clears it via
  // `clearJustSubmitted()` on any edit, so a stale "we've got your answer" can
  // never sit above a selection that now contradicts it.
  const justSubmitted = ref(false);

  const orderBoxItem = computed(() => issue.value?.orderBoxItem ?? null);
  // Still shipped, still read — but a REPORTING TAG only (BE→FE 2026-08-10). The
  // decisions-inbox row shows it as a chip; the card must never branch layout,
  // copy or the response set on it. The member reads `question`.
  const reason = computed(() => issue.value?.reason ?? null);
  /** The leader's own words. THE field on this card — always present, never
   *  empty. Replaces the assembled `message` + per-reason situation line. */
  const question = computed(() => issue.value?.question ?? null);
  /** The leader's photo of the problem. Allowed on ANY question now, not just
   *  quality concerns. */
  const photoUrl = computed(() => issue.value?.photoUrl ?? null);
  /**
   * What the leader can do instead — zero or more. The member answers YES with
   * an `option_index` INTO THIS LIST, so position matters and the contract's own
   * `index` is authoritative (falling back to array position only if a payload
   * ever omits it).
   *
   * `priceCents` and `qty` are present only on the options that change them;
   * both stay null otherwise rather than being back-filled from the item, so a
   * caller can tell "this option leaves the price alone" from "this option costs
   * the same". `label` may name something the catalogue has never recorded —
   * that is the point of the redesign — so never try to resolve it.
   *
   * `photoUrl` is the leader's own shot of THAT alternative (BE→FE 2026-08-14).
   * It exists because the choice is not always nameable: colours and sizes read
   * fine as words, shapes do not, and the catalogue cannot help — photos live on
   * the Product, so every sibling variant shares one image. The key is ALWAYS
   * present on the wire, so an absent photo is null and never a missing key;
   * there is nothing to distinguish from an old payload and no fallback to
   * invent. A mixed list, where only some options carry one, is normal.
   */
  const options = computed(() => {
    const raw = issue.value?.options;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((o) => o && typeof o.label === 'string' && o.label !== '')
      .map((o, position) => ({
        index: Number.isInteger(o.index) ? o.index : position,
        label: o.label,
        priceCents: Number.isFinite(o.priceCents) ? o.priceCents : null,
        qty: Number.isFinite(o.qty) ? o.qty : null,
        photoUrl: typeof o.photoUrl === 'string' && o.photoUrl !== '' ? o.photoUrl : null,
      }));
  });
  const allowedResponses = computed(() =>
    Array.isArray(issue.value?.allowedResponses) ? issue.value.allowedResponses : [],
  );
  /**
   * Are the goods already in her box? — the card's "already collected" state and
   * the gate on every money figure.
   *
   * The item-issue resource exposes no plan-item outcome, so this is derived
   * from the included OrderBoxItem's status via `ItemStatusCategories.GOODS_IN_HAND`
   * (see that list for why the two cannot disagree). Backend consequence:
   * `ItemIssueService::collect()` returns at its COLLECTED branch BEFORE any
   * option is applied — a price option changes nothing and a qty option throws —
   * so no figure stated here would be one the backend charges.
   *
   * Fails CLOSED on an unknown status: without the include we cannot claim the
   * goods are in hand, and the money helpers refuse on their own inputs anyway.
   */
  const isCollected = computed(() => {
    const status = orderBoxItem.value?.status;
    return status != null && ItemStatusCategories.GOODS_IN_HAND.includes(status);
  });

  // ── interaction-v2 reads ────────────────────────────────────────────────────
  const issueStatus = computed(() => issue.value?.status ?? null);
  /** The member's own current answer (null before they answer). */
  const memberResponse = computed(() => issue.value?.memberResponse ?? null);
  /** The member's own latest optional note (null when they sent none). */
  const memberComment = computed(() => issue.value?.memberComment ?? null);
  /** How the CURRENT answer arrived (G1, 2026-07-28): `in_app` on the member's
   *  own path and on legacy rows that predate the column, otherwise the
   *  off-platform channel a leader recorded it through. Null while unanswered. */
  const responseChannel = computed(() => issue.value?.responseChannel ?? null);
  /**
   * The member's own prior pick, echoed back by the resource so a revision opens
   * on what she already told us instead of forcing a re-pick. Null before her
   * first answer, and on an answer that carried no option.
   *
   * Unlike the `memberChosenVariantId` this replaced, an option INDEX cannot be
   * soft-deleted out from under her — the options are frozen in the leader's
   * payload — so a revision always prefills and the old "pick it again"
   * (`needsRechoose`) path is dead.
   */
  const memberOptionIndex = computed(() =>
    Number.isInteger(issue.value?.memberOptionIndex) ? issue.value.memberOptionIndex : null,
  );
  /** Her OWN photo of what arrived, when one exists — a leader-relayed one, or
   *  one she attached herself via `respond`'s `photoToken` (BE→FE 2026-08-11:
   *  the FilePond token endpoint the PWA already uses for wallet receipts and
   *  registration). Read-only here; see `respond` for how one is sent. */
  const memberPhotoUrl = computed(() => issue.value?.memberPhotoUrl ?? null);

  /**
   * The shop she named INSIDE this question, and its name at the moment she
   * named it (BE→FE brief `2026-09-15-the-shop-rides-the-answer.md`).
   *
   * Both are `null` when her answer names no shop. The NAME is a snapshot the
   * backend stores with the answer, not a live lookup — a shop renamed since
   * still reads back as what she picked, which is the right way round for a
   * receipt. The ID is what re-selects the row when she edits her answer.
   */
  const memberSupplierId = computed(() => issue.value?.memberSupplierId ?? null);
  const memberSupplierName = computed(() => issue.value?.memberSupplierName ?? null);

  /**
   * The line's CARTON, when this device happens to hold it.
   *
   * The suggest door's fourth arm needs it — a carton that has moved on cannot
   * take the item back, so the door must not be offered (oos-desk P3-E). This
   * screen's own read includes `orderBoxItem` and NOT its box, so the carton is
   * resolved out of the ORM by `orderBoxId` instead of widening the include.
   *
   * `undefined` WHEN IT IS NOT THERE, deliberately, because that is
   * `canSuggestSupplier`'s documented escape hatch: a caller that cannot say
   * anything about the carton gets the line-only answer rather than a false
   * `false`. A cold deep link straight into this page has no boxes in the store
   * yet, and hiding the door there would be a worse error than offering one the
   * backend might refuse — she can always be told; she cannot always be asked
   * again.
   */
  const orderBox = computed(() => {
    const boxId = orderBoxItem.value?.orderBoxId;
    if (boxId == null) return undefined;
    return useRepo(OrderBox).find(String(boxId)) ?? undefined;
  });
  /** Render-ready history steps (localizable title keys, normalized actors). */
  const timeline = computed(() => timelineSteps(issue.value?.timeline));
  /** True once the member has answered — the form becomes a REVISION. */
  const isRevision = computed(() => memberResponse.value != null);
  /** Can the member submit right now? Mirrors the backend's respond gate. */
  const answerable = computed(() => isIssueAnswerable(issue.value));
  const isResolvedIssue = computed(() => issueStatus.value === ISSUE_STATUS.RESOLVED);

  /**
   * Is the LINE itself out of stock, whatever the ISSUE's own status says?
   *
   * A leader marking a line out of stock in answer to a raised issue RESOLVES
   * the issue — but the line is not done: it keeps her money and stays open to
   * a cancel or a second shop for as long as its carton can still take it back
   * (tips-wave P3-A, owner answer W-K). `isResolvedIssue` alone used to headline
   * that state with «الموضوع خلص» / «...اتظبط خلاص، مفيش حاجة مطلوبة منك
   * دلوقتي» — the same "nothing left to do" claim the money note directly
   * beneath it would then have contradicted.
   *
   * Coerced the way every other status gate in this codebase is: a string
   * `"41"` off the wire must not silently read as "not out of stock".
   */
  const isOutOfStockItem = computed(() => {
    const raw = orderBoxItem.value?.status;
    if (raw == null) return false;
    const status = typeof raw === 'string' ? Number(raw) : raw;
    return status === ItemStatus.OUT_OF_STOCK;
  });

  /**
   * Why the screen is read-only, as an i18n key pair. An out-of-stock line
   * wins over every other reading — the calm "being handled" state and a
   * genuinely resolved issue both claim there is nothing left to do, and
   * that is false here (see `isOutOfStockItem`). Otherwise: a terminal issue
   * is headlined with WHAT happened (read off its terminal timeline step,
   * since the resource exposes no `resolution` field); an open-but-
   * unanswerable issue is the calm "being handled" state.
   */
  const readOnlyTitleKey = computed(() => {
    if (isOutOfStockItem.value) return 'collection.state.outOfStock';
    return isResolvedIssue.value ? 'collection.readOnly.resolvedTitle' : 'collection.state.beingHandled';
  });
  const readOnlyBodyKey = computed(() => {
    if (isOutOfStockItem.value) return 'collection.state.outOfStockBody';
    return isResolvedIssue.value
      ? 'collection.readOnly.resolvedBody'
      : 'collection.state.beingHandledBody';
  });

  /**
   * May she cancel this out-of-stock line RIGHT NOW, and get her held money
   * back? The same carton window `canSuggestSupplier()` and `ItemSheets.vue`'s
   * own `canCancelOutOfStock` read — ACTIVE/READY, not delivery-confirmed
   * (brief `2026-09-15-oos-line-cancel-and-the-hold.md`, owner answer H-M).
   *
   * FAILS CLOSED on an unknown carton, same as `useItemStatus.canCancel`: this
   * screen's own read includes `orderBoxItem` but not its box, so a cold deep
   * link with no boxes loaded yet must not offer a control that can only 422.
   */
  const canCancelOutOfStock = computed(
    () => isOutOfStockItem.value && boxCanTakeItBack(orderBox.value),
  );

  const cancellingOutOfStock = ref(false);

  /**
   * Cancel the line and release its hold — the SAME service call and the SAME
   * carton gate `ItemSheets.vue`'s own out-of-stock cancel uses, so a member
   * reads one behaviour for one action wherever she meets it.
   *
   * Re-reads the ISSUE afterwards (not just the item): the line's fresh status
   * is what flips `isOutOfStockItem` back off and lets the banner above return
   * to its ordinary resolved wording.
   *
   * NO wallet call here, and none added — the balance rule is being rewritten
   * on a separate branch (hold-from-order) and this composable never re-derives
   * one. `ItemSheets.vue`'s own cancel additionally nudges the wallet tab to
   * reload; this surface has no wallet tab to nudge, so it does not try.
   *
   * @returns {Promise<{ok: boolean, message?: string|null}>}
   */
  async function cancelOutOfStockItem() {
    const itemId = orderBoxItem.value?.id;
    if (itemId == null || cancellingOutOfStock.value) return { ok: false };

    cancellingOutOfStock.value = true;
    try {
      const updated = await cancelOrderBoxItem(itemId, null);
      useRepo(OrderBoxItem).save(updated);

      // The carton's totals and her committed balance both moved (a released
      // hold), same as ItemSheets' own cancel — re-read it so the boxes list
      // she came from stays honest. Best-effort: a failed refresh here does not
      // undo a cancel that already succeeded.
      const boxId = orderBoxItem.value?.orderBoxId;
      if (boxId != null) {
        OrderBox.fetchOne(boxId).catch(() => {});
      }

      await refresh();
      return { ok: true };
    } catch (err) {
      // The 422's detail is her own Arabic sentence (the backend stopped
      // sending the English service exception) — the caller prefers it over
      // its own copy exactly as every other refusal on this surface does.
      return { ok: false, message: err?.detail || null };
    } finally {
      cancellingOutOfStock.value = false;
    }
  }
  /** The terminal step's copy key, or null when the timeline has none. */
  const outcomeKey = computed(() =>
    isResolvedIssue.value ? terminalOutcomeKey(issue.value?.timeline) : null,
  );
  /** Is `outcomeKey` a neutral closure (withdrawal), not a real outcome? Gates
   *  the read-only banner's success styling — see `terminalOutcomeIsNeutral`. */
  const outcomeIsNeutral = computed(() =>
    isResolvedIssue.value ? terminalOutcomeIsNeutral(issue.value?.timeline) : false,
  );

  /**
   * Whose court the issue is in, as a TurnPill variant — shared with the history
   * list so one issue never reads as two different states. A revision sits on
   * 'handled': once the member has answered, the ball IS with the leader, and the
   * form is live only because the leader hasn't applied it yet (exactly what the
   * edit hint says). Claiming «قرارك مطلوب» there would be the dishonest option.
   */
  const pillVariant = computed(() => issuePillVariant(issue.value));

  // Item display name for {item} interpolation — reuses the app's SSOT helper so
  // manual (off-catalog) lines resolve correctly.
  const itemName = computed(() => {
    const obi = orderBoxItem.value;
    return obi ? itemDisplayName(obi) : '';
  });

  /**
   * The confirmation copy key — or null when there is nothing to confirm.
   *
   * ONE key for every answer now (decision D2 — see RECORDED_CONFIRM_KEY). It
   * no longer branches on the response, and no longer needs to branch on
   * `isCollected` either: the collected card was only ever special because the
   * three outcome confirms were LOUDLY false there, and they are gone.
   *
   * Still null before any answer is submitted, so the caller keeps guarding it.
   */
  const confirmKey = computed(() => {
    if (submittedRevision.value) return REVISED_CONFIRM_KEY;
    if (submittedResponse.value == null) return null;
    return RECORDED_CONFIRM_KEY;
  });

  const isBusy = computed(() => state.value === 'loading' || state.value === 'submitting');

  // The form is on screen for BOTH 'awaiting' and 'submitting' — submitting only
  // locks the inputs + spins the tapped button. Expressing this as ONE predicate
  // (rather than two page-local checks) keeps the load-bearing invariant with the
  // state machine: the page renders a single <issue-card> instance across both
  // states, so a submit never remounts the card and resets the member's transient
  // selection (chosen variant / typed supplier / typed note). Splitting these
  // back into separate branches is the exact regression this guards against.
  const isDeciding = computed(
    () => state.value === 'awaiting' || state.value === 'submitting',
  );

  /** Bring the store item's `activeIssue` projection in line with what we just
   *  learned here, so the order row stops claiming "قرارك مطلوب" the moment the
   *  member acts (the ball is back with the leader → awaitingMember is false).
   *  No-op if the item isn't loaded (e.g. the card was reached cold via a
   *  notification deep-link).
   *
   *  It must MIRROR the server projection, not just mute it. `activeIssue` is a
   *  HasOne filtered to NON-RESOLVED issues, so for a resolved issue the server
   *  returns null — writing a synthetic `status: 'open'` there would invent a
   *  live issue the server denies, and since a live issue now makes the row
   *  routable, the row would advertise openable work on a closed issue until the
   *  next refetch. Hence: null when the issue is done or gone, otherwise keep the
   *  id (the row can still open it read-only) with its REAL status.
   *
   *  @param {{ gone?: boolean }} [opts] gone: the server refused it (403/404) —
   *    not ours, or the flag is off. Treat as no issue at all.
   */
  function syncItemBadge({ gone = false } = {}) {
    const itemId = orderBoxItem.value?.id;
    if (itemId == null) return;
    const repo = useRepo(OrderBoxItem);
    const existing = repo.find(itemId);
    if (!existing) return;
    const prior = existing.activeIssue;
    const status = issueStatus.value;
    const noLiveIssue = gone || status == null || status === ISSUE_STATUS.RESOLVED;
    repo.save({
      id: itemId,
      activeIssue:
        noLiveIssue || !prior ? null : { ...prior, awaitingMember: false, status },
    });
  }

  /** Derive the resting state from the freshly-read issue. */
  function syncState() {
    // Whatever we just read is more current than the badge on the order row that
    // may have sent the member here. Anything other than awaiting_member — a
    // revision, a terminal issue, the leader's court — means the amber
    // "قرارك مطلوب" chip is stale, so retire it now rather than leaving the member
    // to tap it again and land right back on this same explanation.
    if (issueStatus.value !== ISSUE_STATUS.AWAITING_MEMBER) syncItemBadge();

    if (answerable.value) {
      state.value = 'awaiting';
      return;
    }
    // Read-only wins over any inline submit error: the read-only banner explains
    // the situation properly, and a stale "couldn't save" line under it would
    // invite a retry the server will keep refusing.
    respondError.value = null;
    state.value = 'read-only';
  }

  /**
   * Merge a respond response onto the loaded issue. A custom JSON:API action
   * takes no `include`, so its payload carries fresh ATTRIBUTES but no
   * `orderBoxItem` — replacing outright would blank the item name/price the whole
   * screen is built from.
   */
  function mergeIssue(updated) {
    if (!updated) return;
    const priorItem = issue.value?.orderBoxItem ?? null;
    issue.value = {
      ...issue.value,
      ...updated,
      orderBoxItem: updated.orderBoxItem ?? priorItem,
    };
  }

  async function load(id) {
    issueId.value = id;
    state.value = 'loading';
    loadError.value = null;
    respondError.value = null;
    actionsLocked.value = false;
    roundReportedClosed.value = false;
    submittedResponse.value = null;
    submittedRevision.value = false;
    justSubmitted.value = false;
    try {
      issue.value = await fetchItemIssue(id);
      syncState();
    } catch (err) {
      if (err?.code === ITEM_ISSUE_ALREADY_HANDLED) {
        state.value = 'already-handled';
        syncItemBadge({ gone: true });
      } else {
        loadError.value = err;
        state.value = 'error';
      }
    }
  }

  /**
   * Silently re-read the issue and re-derive the state — for screen focus,
   * pull-to-refresh, and as the "what does the server actually say" probe after a
   * refused submit. No skeleton, and a failed probe never destroys a live form:
   * a network blip mid-refresh leaves what the member is looking at intact.
   *
   * @returns {Promise<boolean>} true when the issue was re-read
   */
  async function refresh() {
    if (issueId.value == null) return false;
    try {
      issue.value = await fetchItemIssue(issueId.value);
      syncState();
      return true;
    } catch (err) {
      if (err?.code === ITEM_ISSUE_ALREADY_HANDLED) {
        syncItemBadge({ gone: true });
        state.value = 'already-handled';
      }
      return false;
    }
  }

  /**
   * Re-read on screen re-entry / pull-to-refresh, so a leader's apply-defer-OOS
   * outcome and any new timeline steps appear (this surface has no realtime
   * push). Deliberately inert on the states a refresh would sabotage: an
   * in-flight submit, the calm already-handled close, and the initial load.
   *
   * It no longer has to protect a confirm end-state, because there is no longer
   * one to protect: a submitted answer rests on 'awaiting'/'read-only' like any
   * other readable card, and re-entering after answering now DOES refresh —
   * which is what picks up the leader acting on the answer she just sent.
   */
  async function refreshIfIdle() {
    if (state.value !== 'awaiting' && state.value !== 'read-only' && state.value !== 'error') {
      return false;
    }
    return refresh();
  }

  function retry() {
    if (issueId.value != null) return load(issueId.value);
    return Promise.resolve();
  }

  /** Drop the inline confirmation. The card calls this the moment the member
   *  edits anything, so «تمام، عدّلنا ردك» never sits above a selection that no
   *  longer matches what was sent. */
  function clearJustSubmitted() {
    justSubmitted.value = false;
  }

  /**
   * Submit one answer — a first answer or a revision, same call (the backend is
   * latest-wins). `response` must be in `allowedResponses`.
   *
   * Takes the FE-shaped `{ optionIndex, note, photoToken }` and emits the wire
   * shape `{ option_index, note, photo_token }`, so no component has to know the
   * snake_case contract. Three rules live here:
   *
   *  - `option_index` is sent only when it is a real integer. A YES on a
   *    question that offers options MUST carry one or the server 422s; a YES on
   *    a question with none must NOT.
   *  - `note` HAS THREE STATES ON THE WIRE, not two — see `noteCleared` below.
   *  - `photo_token` is the FilePond `serverId` from `POST /api/v1/filepond` —
   *    the same contract wallet receipts and registration already use (BE→FE
   *    2026-08-11). Omitted when blank: she cannot re-send a consumed token, and
   *    an omitted key now carries the stored photo forward.
   *
   * THE NOTE'S THREE STATES (BE→FE 2026-08-12, `normalizeMemberPayload()`).
   * Omission is no longer a stylistic choice — it is the KEEP signal, and the
   * backend distinguishes it from a blank by `array_key_exists`, not by value:
   *
   *   untouched → OMIT the key       → the stored note is carried forward
   *   written   → send the trimmed string
   *   cleared   → send an explicit '' → the ONLY thing that clears a stored note
   *
   * The caller expresses the third one with `noteCleared: true`. It is a
   * separate flag, not a magic `note: ''`, precisely because getting this
   * backwards destroys a note the member wrote: a blank string is what an
   * untouched empty textarea hands you by accident, whereas nobody sets
   * `noteCleared` by accident. Forgetting the flag keeps her note; the
   * destructive reading needs an explicit opt-in. A non-blank `note` always
   * wins over the flag — text is not a clear.
   *
   * ONE THING THE CALLER MUST KNOW ABOUT THE TOKEN: it is SINGLE-USE and expires
   * after 60 minutes. Never re-send one that has already been submitted — the
   * server 422s a consumed or expired token rather than dropping it silently,
   * which is correct but means a stale token in hand turns a good answer into a
   * refusal.
   *
   * Server-confirmed, never optimistic.
   *
   * @param {string} response one of `allowedResponses`
   * ── A SHOP MAY RIDE ANY ANSWER (2026-09-15, owner answer H-G) ──────────────
   * `payload.supplier_id` is a roster id, stored on the answer with the shop's
   * name at that moment. It makes a WAIT legal even on a question that did not
   * offer «استناني» — the one named bypass, which moved here from the tip door.
   * A WAIT with NO shop on such a question is still a 422.
   *
   * Like `note`, it is LATEST-WINS WITH A KEEP: leaving `supplierId` out of a
   * revision keeps the shop already on the answer, and passing `null`
   * explicitly clears it. That is why the default is `undefined` rather than
   * `null` here — the two mean different things on the wire, and collapsing
   * them would silently wipe her shop every time she edited her note.
   *
   * @param {{ optionIndex?: number|null, note?: string|null, noteCleared?: boolean,
   *   photoToken?: string|null, supplierId?: number|null }} [payload]
   */
  async function respond(
    response,
    {
      optionIndex = null,
      note = null,
      noteCleared = false,
      photoToken = null,
      supplierId = undefined,
    } = {},
  ) {
    if (state.value !== 'awaiting') return {};
    const payload = {};
    if (Number.isInteger(optionIndex)) payload.option_index = optionIndex;
    // OMITTED keeps what is on record; an explicit null clears it. Never
    // collapsed into one branch — see the docblock.
    if (supplierId !== undefined) {
      payload.supplier_id = supplierId == null ? null : Number(supplierId);
    }
    const trimmedNote = typeof note === 'string' ? note.trim() : '';
    if (trimmedNote !== '') payload.note = trimmedNote;
    else if (noteCleared === true) payload.note = '';
    const trimmedToken = typeof photoToken === 'string' ? photoToken.trim() : '';
    if (trimmedToken !== '') payload.photo_token = trimmedToken;
    // Captured BEFORE the POST: the merge below sets memberResponse, which would
    // make every submit look like a revision by the time we pick the copy.
    const wasRevision = isRevision.value;
    respondError.value = null;
    actionsLocked.value = false;
    state.value = 'submitting';
    try {
      const updated = await respondToItemIssue(issueId.value, response, payload);
      mergeIssue(updated);
      submittedResponse.value = response;
      submittedRevision.value = wasRevision;
      // The respond payload carries the issue's fresh status, so this writes the
      // real one — 'open' when the answer is now with the leader (the row stays
      // openable read-only), null if the leader's apply already resolved it.
      syncItemBadge();
      // No confirm state to cross into: the card flips in place, so the machine
      // rests wherever the FRESH issue says it should. Normally that is back on
      // 'awaiting' — her answer is revisable until the leader applies it, and
      // that is exactly what the edit hint promises — but a leader-apply that
      // landed in the same breath comes back RESOLVED, and syncState() reads
      // that off the merged issue and goes 'read-only'. Hard-coding either one
      // would make the card lie in the other case.
      syncState();
      justSubmitted.value = true;
    } catch (err) {
      if (err?.code === ITEM_ISSUE_ALREADY_HANDLED) {
        // Lost the race to a leader-apply — the item is already sorted.
        syncItemBadge({ gone: true });
        state.value = 'already-handled';
      } else if (err?.code === ITEM_ISSUE_BLOCKED) {
        // The server refused, and its own member-facing Arabic detail is the
        // message. But WHY it refused decides what the card does next, and the
        // 409s are indistinguishable by text, so re-read the issue and let its
        // status decide rather than pattern-matching a localized string.
        respondError.value = err?.detail || null;
        state.value = 'awaiting';

        // A 409 no longer means "the day closed" (close-now, 2026-09-24): she
        // keeps answering after a COMPLETED plan, for 12 hours. Only the
        // `plan-closed` code — a CANCELLED or deleted plan — says the answer can
        // never land. Set BEFORE the confirming read so the card stops claiming
        // the round is open now, not one round-trip later, and not only when the
        // confirmation happens to succeed.
        const planClosed = err.status === 409 && err.apiCode === ITEM_ISSUE_PLAN_CLOSED;
        if (planClosed) roundReportedClosed.value = true;
        const refreshed = await refresh();

        // A RESOLVED issue: refresh() already routed the card to read-only (the
        // leader applied it, or the 12-hour closer did). A FAILED re-read leaves
        // the backend's detail inline and the buttons live. Nothing more to do.
        if (!refreshed || !answerable.value) return {};

        // Still answerable after a plan-closed 409: the plan will never reopen,
        // so retrying is not "might work later", it is "will never work". Lock
        // the action stack rather than re-offer a button the server will keep
        // refusing. Only on a CONFIRMED re-read — disabling fails open.
        if (planClosed) {
          actionsLocked.value = true;
          return {};
        }

        // Still answerable after any other 409: the question is still hers, so
        // the buttons stay and she is told to try again. The backend's detail
        // is dropped — both its 409 texts say the decision is over, which the
        // fresh issue just contradicted. A 422 (response_not_allowed) keeps its
        // inline detail: the re-read `allowedResponses` may legitimately
        // differ, and the message says what to change.
        if (err.status === 409) {
          respondError.value = null;
          return { retry: true };
        }
      } else {
        // Network / 5xx — no member-facing detail; a generic retryable inline
        // error (the caller maps this null to common.errorTryAgain).
        respondError.value = null;
        state.value = 'awaiting';
        return { networkError: true };
      }
    }
    return {};
  }

  return {
    // state
    state,
    issue,
    loadError,
    respondError,
    actionsLocked,
    roundReportedClosed,
    submittedResponse,
    submittedRevision,
    justSubmitted,
    isBusy,
    isDeciding,
    // derived
    orderBoxItem,
    orderBox,
    memberSupplierId,
    memberSupplierName,
    itemName,
    reason,
    question,
    photoUrl,
    options,
    allowedResponses,
    isCollected,
    confirmKey,
    // interaction-v2
    issueStatus,
    memberResponse,
    memberOptionIndex,
    memberComment,
    memberPhotoUrl,
    responseChannel,
    timeline,
    isRevision,
    answerable,
    isResolvedIssue,
    readOnlyTitleKey,
    readOnlyBodyKey,
    outcomeKey,
    outcomeIsNeutral,
    pillVariant,
    isOutOfStockItem,
    canCancelOutOfStock,
    cancellingOutOfStock,
    // actions
    load,
    refresh,
    refreshIfIdle,
    retry,
    respond,
    clearJustSubmitted,
    cancelOutOfStockItem,
  };
}
